import StorageManager from "../../utils/StorageManager";

/**
 * logoPot — the fire in the logo's cup (GDD "POT upgrade stages").
 *
 * The GDD: fire trails "fill up" the Pot as Fireball coins land; the fire grows
 * through 4 stages (small -> bigger -> bigger -> biggest), and the 5th is WIN —
 * the feature triggering ("POT should play small blast animation around the
 * fire and logo"). Hold & Win itself is the server's (a % on its side); the
 * stages are OURS, cosmetic, to build the tension (contract §14.4 — no API
 * field), so they are kept here and in localStorage.
 *
 *   a trail lands   +1 point; enough points and the fire goes up a stage.
 *                   Each stage takes a few coins more than the last, with a
 *                   little randomness, so it reads natural — not a step per
 *                   coin. Stage 4 is the top: it stays there.
 *   H&W triggers    WIN, from wherever it stood (the Random Pot included):
 *                   the fire rises to stage 4 (lvl4_in from stage 3, a blend
 *                   from lower), then the BLAST (logo `logo_blast`) plays over
 *                   it (GDD: "4th stage is a WIN stage", 2026-09-30).
 *   H&W ends        back to stage 1, empty.
 *
 * Both rooms' logos (base and free spins) show the same Pot: they subscribe
 * with onLogoPotChange.
 */

export const LOGO_POT_STAGES = 4;
export const LOGO_POT_WIN = 5;

// Coins to go from one stage to the next (1->2, 2->3, 3->4), before the ±1.
// Starting values, to be tuned by eye (Vladimir, 2026-09-29: "okay for start").
const COINS_PER_STAGE = [4, 5, 6];
const JITTER = 1;

const listeners = new Set();

const coinsFor = (stage) => {
  const base = COINS_PER_STAGE[stage - 1] ?? COINS_PER_STAGE[COINS_PER_STAGE.length - 1];
  return base + Math.round((Math.random() * 2 - 1) * JITTER);
};

const fresh = () => ({ stage: 1, points: 0, next: coinsFor(1) });

const valid = (pot) =>
  pot &&
  Number.isInteger(pot.stage) &&
  pot.stage >= 1 &&
  pot.stage <= LOGO_POT_WIN &&
  Number.isFinite(pot.points) &&
  Number.isFinite(pot.next);

let pot = null;
const current = () => {
  if (!pot) {
    const stored = StorageManager.loadLogoPot();
    pot = valid(stored) ? stored : fresh();
  }
  return pot;
};

// `shown` is what the listeners are told — the stage, or LOGO_POT_WIN for the
// blast (which leaves the stage as it is).
const commit = (next, animate, shown = next.stage) => {
  pot = next;
  StorageManager.saveLogoPot(pot);
  listeners.forEach((fn) => fn(shown, animate));
};

/** The fire stage to show now (1–4). */
export const logoPotStage = () => Math.min(current().stage, LOGO_POT_STAGES);

/**
 * A coin's trail reached the cup. Returns the new stage if this one raised it,
 * else null. Nothing moves at the top or during a WIN.
 */
export const addLogoPotPoint = () => {
  const p = current();
  // Nothing fills a cup that has just blown (the bonus is on its way).
  if (p.won || p.stage >= LOGO_POT_STAGES) return null;
  const points = p.points + 1;
  if (points < p.next) {
    pot = { ...p, points };
    StorageManager.saveLogoPot(pot);
    return null;
  }
  const stage = p.stage + 1;
  commit({ stage, points: 0, next: coinsFor(stage) }, true);
  return stage;
};

/** Hold & Win has triggered: WIN, whatever the stage was. */
export const logoPotWin = () => {
  // if (current().stage === LOGO_POT_WIN) return;
  // commit({ ...current(), stage: LOGO_POT_WIN }, true);
  // The blast, once — the stage stays where it was (the fire keeps burning
  // at it until the bonus is over and the cup resets).
  const p = current();
  if (p.won) return;
  // commit({ ...p, won: true }, true, LOGO_POT_WIN);
  // The fire rises to stage 4 first, then blasts (GDD: "4th stage is a WIN
  // stage") — so the cup IS at stage 4 until the bonus is over (a room that
  // opens meanwhile shows it there). The logo does the rise (setLogoStage).
  commit({ ...p, stage: LOGO_POT_STAGES, won: true }, true, LOGO_POT_WIN);
};

/** Hold & Win is over: an empty cup again. */
export const resetLogoPot = () => {
  const p = current();
  if (p.stage === 1 && p.points === 0 && !p.won) return;
  commit(fresh(), false);
};

/** fn(stage, animate) on every stage change. Returns the unsubscribe. */
export const onLogoPotChange = (fn) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};
